import { decorate, observable } from 'mobx';
import setMarkdownTheme from '../themes/markdown';

type ThemeType = 'light' | 'dark';

const MODEL_VERSION = '9/27/2019';
const KEY_PREFIX = 'prefs';

class PreferenceStore {
    private storage: Storage = window.localStorage;

    public theme: ThemeType;
    public recentlyViewedFamilies: any[];
    public recentlyViewedEnvironments: any[];
    public recentlyViewedDecks: any[];
    public recentlyViewedParamSets: any[];
    public recentlyViewedDeployments: any[];

    constructor() {
        // Check version
        const currentVersion = this.storage.getItem(`${KEY_PREFIX}:modelVersion`);
        if (currentVersion !== MODEL_VERSION) {
            this.clearPreferences();
            this.storage.setItem(`${KEY_PREFIX}:modelVersion`, MODEL_VERSION);
        }

        this.recentlyViewedFamilies = JSON.parse(this.storage.getItem(`${KEY_PREFIX}:recentlyViewedFamilies`) || '[]');
        this.recentlyViewedEnvironments = JSON.parse(this.storage.getItem(`${KEY_PREFIX}:recentlyViewedEnvironments`) || '[]');
        this.recentlyViewedDecks = JSON.parse(this.storage.getItem(`${KEY_PREFIX}:recentlyViewedDecks`) || '[]');
        this.recentlyViewedParamSets = JSON.parse(this.storage.getItem(`${KEY_PREFIX}:recentlyViewedParamSets`) || '[]');
        this.recentlyViewedDeployments = JSON.parse(this.storage.getItem(`${KEY_PREFIX}:recentlyViewedDeployments`) || '[]');
        this.theme = this.storage.getItem(`${KEY_PREFIX}:theme`) as ThemeType || 'light';
        setMarkdownTheme(this.theme);
    }

    setTheme(theme: ThemeType) {
        this.theme = theme;
        setMarkdownTheme(this.theme);
        this.storage.setItem(`${KEY_PREFIX}:theme`, theme);
    }

    addRecentlyViewedFamily(id: string, name: string, description: string) {
        const recentlyViewed = this.recentlyViewedFamilies.slice();
        const index = recentlyViewed.findIndex(item => item.id === id);
        if (index !== -1) {
            // Remove old entry
            recentlyViewed.splice(index, 1);
        }
        // Add to front of array
        recentlyViewed.unshift({
            id,
            name,
            description
        });
        // Save last 5 items in array
        this.recentlyViewedFamilies = recentlyViewed.slice(0, 5);
        this.storage.setItem(`${KEY_PREFIX}:recentlyViewedFamilies`, JSON.stringify(this.recentlyViewedFamilies));
    }

    addRecentlyViewedEnvironment(environmentName: string, region: string) {
        const recentlyViewed = this.recentlyViewedEnvironments.slice();
        const index = recentlyViewed.findIndex(item => item.environmentName === environmentName && item.region === region);
        if (index !== -1) {
            // Remove old entry
            recentlyViewed.splice(index, 1);
        }
        // Add to front of array
        recentlyViewed.unshift({ environmentName, region });
        // Save last 5 items in array
        this.recentlyViewedEnvironments = recentlyViewed.slice(0, 10);
        this.storage.setItem(`${KEY_PREFIX}:recentlyViewedEnvironments`, JSON.stringify(this.recentlyViewedEnvironments));
    }

    addRecentlyViewedDeck(deckName: string) {
        const recentlyViewed = this.recentlyViewedDecks.slice();
        const index = recentlyViewed.findIndex(item => item === deckName);
        if (index !== -1) {
            // Remove old entry
            recentlyViewed.splice(index, 1);
        }
        // Add to front of array
        recentlyViewed.unshift(deckName);
        // Save last 5 items in array
        this.recentlyViewedDecks = recentlyViewed.slice(0, 10);
        this.storage.setItem(`${KEY_PREFIX}:recentlyViewedDecks`, JSON.stringify(this.recentlyViewedDecks));
    }

    addRecentlyViewedParamSet(name: string, version: string, accountId: string, region: string) {
        const recentlyViewed = this.recentlyViewedParamSets.slice();
        const index = recentlyViewed.findIndex(item => item.name === name && item.accountId === accountId && item.region === region);
        if (index !== -1) {
            // Remove old entry
            recentlyViewed.splice(index, 1);
        }
        // Add to front of array
        recentlyViewed.unshift({ name,  version, accountId, region});
        // Save last 5 items in array
        this.recentlyViewedParamSets = recentlyViewed.slice(0, 10);
        this.storage.setItem(`${KEY_PREFIX}:recentlyViewedParamSets`, JSON.stringify(this.recentlyViewedParamSets));
    }

    addRecentlyViewedDeployment(name: string, accountId: string, region: string, global: boolean) {
        const recentlyViewed = this.recentlyViewedDeployments.slice();
        const index = recentlyViewed.findIndex(item => item.name === name && item.accountId === accountId && item.region === region && item.global === global);
        if (index !== -1) {
            // Remove old entry
            recentlyViewed.splice(index, 1);
        }
        // Add to front of array
        recentlyViewed.unshift({ name, accountId, region, global});
        // Save last 5 items in array
        this.recentlyViewedDeployments = recentlyViewed.slice(0, 10);
        this.storage.setItem(`${KEY_PREFIX}:recentlyViewedDeployments`, JSON.stringify(this.recentlyViewedDeployments));
    }

    private clearPreferences() {
        Object.keys(this.storage).filter(key => key.startsWith(KEY_PREFIX)).forEach(key => this.storage.removeItem(key));
    }
}

decorate(PreferenceStore, {
    theme: observable,
});

export default PreferenceStore;